import { useEffect, useState } from 'react'
import {
  observeActivityExploreCommentsLive,
  observeInstagramReelCommentsLive,
  observeNumberOfActivityExploreCommentsLive,
  observeNumberOfInstagramReelCommentsLive,
  observeNumberOfPodcastCommentsLive,
  observeNumberOfSymptomCommentsLive,
  observeNumberOfWebinarSuggestionCommentsLive,
  observeNumberOfWebinarCommentsLive,
  observePodcastCommentsLive,
  observeSymptomCommentsLive,
  observeWebinarSuggestionCommentsLive,
  observeWebinarCommentsLive,
} from '@domain/features/activity-comments/activity-comments-feature'
import type { CommentsOnType } from '@domain/models/activity-comments'
import { CommentsOn } from '@domain/models/activity-comments'
import type { ActivityComment } from '@domain/models/activity-comments'

interface UseActivityExploreCommentsOptions {
  descending?: boolean
  limitCount?: number
  enabled?: boolean
  commentsOn?: CommentsOnType
  symptomId?: string
}

export function useActivityExploreComments(
  targetId: string | undefined,
  options?: UseActivityExploreCommentsOptions,
) {
  const commentsOn = options?.commentsOn ?? CommentsOn.ActivityExplore
  const isSymptomConfigurationInvalid =
    commentsOn === CommentsOn.Symptom
    && Boolean(targetId)
    && options?.enabled !== false
    && !options?.symptomId

  const key = !isSymptomConfigurationInvalid && targetId && options?.enabled !== false
    ? `${commentsOn}:${options?.symptomId ?? '-'}:${targetId}:${options?.descending === false ? 'asc' : 'desc'}:${options?.limitCount ?? 'all'}`
    : null

  const [state, setState] = useState<{
    key: string | null
    comments: ActivityComment[]
    count: number
    hasLoaded: boolean
    error: string | null
  }>({
    key: null,
    comments: [],
    count: 0,
    hasLoaded: false,
    error: null,
  })

  useEffect(() => {
    if (!targetId || !key) {
      return
    }

    const handleSuccess = (nextComments: ActivityComment[]) => {
      setState((previousState) => ({
        ...previousState,
        key,
        comments: nextComments,
        hasLoaded: true,
        error: null,
      }))
    }

    const handleCountSuccess = (nextCount: number) => {
      setState((previousState) => ({
        ...previousState,
        key,
        count: nextCount,
        error: null,
      }))
    }

    const handleError = (nextError: Error) => {
      setState((previousState) => ({
        ...previousState,
        key,
        hasLoaded: true,
        error: nextError.message,
      }))
    }

    let unsubscribeComments: (() => void) = () => {}
    let unsubscribeCount: (() => void) = () => {}
    const commonOptions = {
      descending: options?.descending,
      limitCount: options?.limitCount,
    }

    if (commentsOn === CommentsOn.Webinar) {
      unsubscribeComments = observeWebinarCommentsLive(
        targetId,
        handleSuccess,
        handleError,
        commonOptions,
      )
      unsubscribeCount = observeNumberOfWebinarCommentsLive(
        targetId,
        handleCountSuccess,
        handleError,
      )
    } else if (commentsOn === CommentsOn.Podcast) {
      unsubscribeComments = observePodcastCommentsLive(
        targetId,
        handleSuccess,
        handleError,
        commonOptions,
      )
      unsubscribeCount = observeNumberOfPodcastCommentsLive(
        targetId,
        handleCountSuccess,
        handleError,
      )
    } else if (commentsOn === CommentsOn.InstagramReel) {
      unsubscribeComments = observeInstagramReelCommentsLive(
        targetId,
        handleSuccess,
        handleError,
        commonOptions,
      )
      unsubscribeCount = observeNumberOfInstagramReelCommentsLive(
        targetId,
        handleCountSuccess,
        handleError,
      )
    } else if (commentsOn === CommentsOn.WebinarSuggestion) {
      unsubscribeComments = observeWebinarSuggestionCommentsLive(
        targetId,
        handleSuccess,
        handleError,
        commonOptions,
      )
      unsubscribeCount = observeNumberOfWebinarSuggestionCommentsLive(
        targetId,
        handleCountSuccess,
        handleError,
      )
    } else if (commentsOn === CommentsOn.Symptom) {
      const symptomId = options?.symptomId
      if (!symptomId) {
        return
      }

      unsubscribeComments = observeSymptomCommentsLive(
        symptomId,
        targetId,
        handleSuccess,
        handleError,
        commonOptions,
      )
      unsubscribeCount = observeNumberOfSymptomCommentsLive(
        symptomId,
        targetId,
        handleCountSuccess,
        handleError,
      )
    } else {
      unsubscribeComments = observeActivityExploreCommentsLive(
        targetId,
        handleSuccess,
        handleError,
        commonOptions,
      )
      unsubscribeCount = observeNumberOfActivityExploreCommentsLive(
        targetId,
        handleCountSuccess,
        handleError,
      )
    }

    return () => {
      unsubscribeComments()
      unsubscribeCount()
    }
  }, [
    commentsOn,
    key,
    options?.descending,
    options?.limitCount,
    options?.symptomId,
    targetId,
  ])

  if (!key) {
    return {
      comments: [],
      count: 0,
      loading: false,
      error: isSymptomConfigurationInvalid
        ? 'Symptom id is required to load symptom comments.'
        : null,
    }
  }

  const isCurrentState = state.key === key

  return {
    comments: isCurrentState ? state.comments : [],
    count: isCurrentState ? state.count : 0,
    loading: isCurrentState ? !state.hasLoaded : true,
    error: isCurrentState ? state.error : null,
  }
}
